/**
 * RFC 7807 problem details — the single error shape this API produces.
 * Mirrors `components.schemas.Problem` in api-spec/openapi.yaml.
 */

export interface FieldError {
  /** JSON pointer to the offending field, e.g. `/attributionType`. */
  readonly field: string;
  /** Machine-readable rule identifier. Clients localise from this, never from `message`. */
  readonly code: string;
  readonly message?: string;
}

export interface Problem {
  readonly type: string;
  readonly title: string;
  readonly status: number;
  readonly detail?: string;
  readonly instance?: string;
  readonly requestId?: string;
  readonly errors?: readonly FieldError[];
}

export class ApiError extends Error {
  readonly problem: Problem;

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.problem = problem;
  }

  get status(): number {
    return this.problem.status;
  }

  get requestId(): string | undefined {
    return this.problem.requestId;
  }

  get fieldErrors(): readonly FieldError[] {
    return this.problem.errors ?? [];
  }

  /** True when the failure is worth retrying without changing the request. */
  get isTransient(): boolean {
    return this.status >= 500 || this.status === 429;
  }
}

/** A network or parse failure, where the server never produced a problem document. */
export class TransportError extends Error {
  override readonly cause: unknown;

  constructor(message: string, cause: unknown) {
    super(message);
    this.name = 'TransportError';
    this.cause = cause;
  }
}

function isProblem(value: unknown): value is Problem {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Problem).title === 'string' &&
    typeof (value as Problem).status === 'number'
  );
}

/**
 * Build a `Problem` from a failed response, tolerating servers and proxies that
 * return HTML or an empty body on error. A 502 from Nginx must not surface as a
 * JSON parse error — the status is the useful information.
 */
export async function problemFromResponse(response: Response): Promise<Problem> {
  const fallback: Problem = {
    type: 'about:blank',
    title: response.statusText || 'Request failed',
    status: response.status,
    ...(response.headers.get('x-request-id')
      ? { requestId: response.headers.get('x-request-id') as string }
      : {}),
  };

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) return fallback;

  try {
    const body: unknown = await response.json();
    return isProblem(body) ? { ...fallback, ...body } : fallback;
  } catch {
    return fallback;
  }
}
